/**
 * @file 基于 axios 的 http 请求工具
 * @inheritDoc <https://github.com/axios/axios#request-config>
 *
 * 自定义配置项:
 * @param showError Boolean 接口请求失败时是否显示异常信息。默认为 true
 * @param errorMsg {string} 请求接口失败时显示的提示信息。默认为 `网络异常，请稍候重试`
 * @param withUserId {string} 请求接口失败时显示的提示信息。可选值: ['PARAMS', 'DATA']
 *
 * 异常处理:
 * 将异常分为 http 异常和代码逻辑异常两种情况，针对两种异常情况会有不同的处理
 * - 代码逻辑异常: 通过后端接口的 code 进行判断，非 0 情况视为异常
 * - http 异常: http 状态码不等于 200 时视为异常
 */

/**
 * @BUG
 * Axios 目前锁定 0.18.1 版本，因为更新后的 axios 不再支持自定义属性，详见 issues:
 * - <https://github.com/axios/axios/issues/2438>
 * - <https://github.com/axios/axios/issues/2295>
 * - <https://github.com/axios/axios/blob/master/lib/core/mergeConfig.js#L18-L42>
 */

import Axios from 'axios';
import Store from '@/store/';
import Router from '@/router/';
import { Message as ElMessage } from 'element-ui';
import Permission from './Permission';

/**
 * 开发环境下在控制台打印日志
 * @param data any 要打印的内容
 * @param title string 输出的标题
 */
const reportWhenDeveloping = (data, title = `AXIOS ERROR`) => {
  if (process.env.NODE_ENV === 'development') {
    /* eslint-disable no-console */
    console.error(`!!! ${title} !!!`);
    /* eslint-disable no-console */
    console.warn('--- Log Start ---');
    console.log(data);
    /* eslint-disable no-console */
    console.warn('--- Log End ---');
  }
};

const instance = Axios.create({
  baseURL: process.env.VUE_APP_BASE_URL_API,
  // @TODO: 需要改小
  timeout: 60000,
  withCredentials: true,
  responseType: 'json'
});

instance.interceptors.request.use(config => {
  config.showError = config.hasOwnProperty('showError') ? config.showError : true;
  config.errorMsg = config.hasOwnProperty('errorMsg') ? config.errorMsg : `网络异常，请稍候重试`;

  // 添加 userId 信息
  if (config.hasOwnProperty('withUserId')) {
    let userId = Store.getters['user/userId'];

    if (userId) {
      if (config.withUserId === 'PARAMS')
        config.params ? (config.params.userId = userId) : (config.params = { userId });
      if (config.withUserId === 'DATA')
        config.data ? (config.data.userId = userId) : (config.data = { userId });
    } else {
      reportWhenDeveloping('No available user id.', 'NO USER ID');
    }
  }

  // 添加用户的认证信息
  if (process.env.VUE_APP_SUPPORT_USER_SYSTEM === 'T') {
    if (!Store.state.user.token) {
      Permission.login();
    } else {
      config.headers['Authorization'] = Store.state.user.token;
    }
  }

  return config;
});

instance.interceptors.response.use(
  res => {
    // 正常情况
    if (res.data && res.data.code === 0) return res.data;

    // ↓ 异常情况
    reportWhenDeveloping(res, `MAYBE AN ABNORMAL RESPONSE`);

    // 如果接口允许显示异常
    // - 如果后端提供了信息则使用后端的信息
    // - 后端没有提供信息则使用默认的异常信息
    if (res.config && res.config.showError) {
      const message =
        res.data && res.data.message && typeof res.data.message === 'string'
          ? res.data.message
          : res.config.errorMsg;
      ElMessage({ message, type: 'error' });
    }

    return Promise.reject(res.data);
  },
  err => {
    const res = err.response;
    reportWhenDeveloping(res, `HTTP REQUEST ERROR`);

    // http 504 异常
    if (+res.status === 504) {
      ElMessage({ message: '网络连接超时，请稍候重试', type: 'error' });
    }
    // 用户认证相关异常
    else if (process.env.VUE_APP_SUPPORT_USER_SYSTEM === 'T' && res.data && res.data.code) {
      // 9526
      if (+res.data.code === 9526) {
        Permission.login();
      }
      // 9527
      else if (+res.data.code === 9527) {
        ElMessage({ message: '登录信息失效，请重新登录', type: 'error' });
        setTimeout(() => {
          Permission.logout();
        }, 1000);
      }
      // 9528
      else if (+res.data.code === 9528) {
        Router.push({ name: 'Unavailable' });
      }
    }
    // 其他异常
    else {
      // 如果接口允许显示异常，则显示预设的异常信息
      if (res.config && res.config.showError) {
        ElMessage({ message: res.config.errorMsg, type: 'error' });
      }
    }

    return Promise.reject(err.response);
  }
);

const install = Vue => {
  Object.defineProperty(Vue.prototype, '$http', { value: instance });
};

export const http = instance;

export default install;
